import isNil from 'lodash/isNil'
import {
  UserDisplayStatus,
  UserListRow,
} from '../../api/dto/user-invitation.dto'
import { getRemovableRoles } from '../UserManagement/accessRemovalBuckets'
import { isHrAdminRbacRoleName, joinNames } from '../UserManagement/userDisplay'

const key = (email: string): string => email.trim().toLowerCase()

/**
 * Addresses the edit takes off the profile, compared case-insensitively.
 * `stillListed` covers another list on the same profile, such as CC, which
 * keeps the address invoiced.
 */
export const droppedRecipients = (
  before: string[],
  after: string[],
  stillListed: string[] = []
): string[] => {
  const kept = new Set([...after, ...stillListed].map(key))
  return before.filter((email) => !kept.has(key(email)))
}

/**
 * `access` loses more than invoices (HR Admin, or Finance Manager itself),
 * `invoices` only stops getting them, and `stillInvoiced` keeps the company
 * through another of its billing profiles.
 */
export type RecipientRemovalKind = 'access' | 'invoices' | 'stillInvoiced'

export interface RecipientRemovalLine {
  email: string
  text: string
  kind: RecipientRemovalKind
  /** The companies the line is about, for grouping the invoices-only people. */
  companyNames: string
}

export interface RemovedCompany {
  clientPublicKey: string
  companyName: string
}

/**
 * Taking someone off a company also takes it off their company list. That list
 * is shared by every role, so an HR Admin loses the company too. Finance
 * Manager goes once nothing bills them, the same rule the server applies.
 *
 * @returns their line, and whether their company access comes off with it
 */
export const describeRecipientRemoval = (
  row: UserListRow | undefined,
  companies: RemovedCompany[],
  canRevoke: boolean
): { text: string; revokes: boolean; kind: RecipientRemovalKind } => {
  const names = joinNames(companies.map((company) => company.companyName))
  const it = companies.length === 1 ? 'it' : 'them'
  const invoices = `They'll stop getting ${names} invoices`

  // Only an active account holds access. An invitee's roles are staged and go
  // with the invitation.
  const isActive = !isNil(row) && row.displayStatus === UserDisplayStatus.Active
  const isHrAdmin =
    isActive &&
    (row.rbacRoles ?? []).some((role) => isHrAdminRbacRoleName(role.name))
  const isFinanceManager = isActive && getRemovableRoles(row).length > 0
  if (!canRevoke || (!isHrAdmin && !isFinanceManager)) {
    return { text: `${invoices}.`, revokes: false, kind: 'invoices' }
  }

  const removed = new Set(companies.map((company) => company.clientPublicKey))
  const isLastCompany =
    isFinanceManager &&
    row.billingClientPublicKeys.every((held) => removed.has(held))

  if (isHrAdmin && isLastCompany) {
    return {
      text: `${invoices}, lose HR Admin access to ${it}, and will no longer be a Finance Manager.`,
      revokes: true,
      kind: 'access',
    }
  }
  if (isHrAdmin) {
    return {
      text: `${invoices} and lose HR Admin access to ${it}.`,
      revokes: true,
      kind: 'access',
    }
  }
  if (isLastCompany) {
    return {
      text: `${invoices} and will no longer be a Finance Manager.`,
      revokes: true,
      kind: 'access',
    }
  }
  return { text: `${invoices}.`, revokes: true, kind: 'invoices' }
}

export interface RecipientRemovalPlan {
  /** One line per removed address, saying what it costs them. */
  lines: RecipientRemovalLine[]
  /** Addresses whose company access comes off with the removal. */
  revocable: string[]
}

interface PlanRecipientRemovalParams {
  removedEmails: string[]
  /** Roster rows for the removed addresses. */
  rows: UserListRow[]
  clientPublicKey: string | null
  companyName: string
  /** Addresses another active profile of the same company still lists. */
  stillInvoiced: ReadonlySet<string>
  /** False when this admin may not change access, so only invoices stop. */
  canRevoke: boolean
}

/** Every address taken off one company, each with its line. */
export const planRecipientRemoval = ({
  removedEmails,
  rows,
  clientPublicKey,
  companyName,
  stillInvoiced,
  canRevoke,
}: PlanRecipientRemovalParams): RecipientRemovalPlan => {
  const rowByEmail = new Map(rows.map((row) => [key(row.email), row]))
  const lines: RecipientRemovalLine[] = []
  const revocable: string[] = []

  const seen = new Set<string>()
  for (const email of removedEmails) {
    if (seen.has(key(email))) continue
    seen.add(key(email))

    if (stillInvoiced.has(key(email))) {
      lines.push({
        email,
        text: `They still get ${companyName} invoices from its other billing profile.`,
        kind: 'stillInvoiced',
        companyNames: companyName,
      })
      continue
    }

    const { text, revokes, kind } = describeRecipientRemoval(
      rowByEmail.get(key(email)),
      [{ clientPublicKey: clientPublicKey ?? '', companyName }],
      canRevoke && !isNil(clientPublicKey)
    )
    lines.push({ email, text, kind, companyNames: companyName })
    if (revokes) revocable.push(email)
  }

  return { lines, revocable }
}
